import { defineStore, acceptHMRUpdate } from 'pinia';
import { ref } from 'vue';
import {
    login, // 账号登录 /_hotel/login
    getBrandShopList, // 获取门店列表 /_hotel/getBrandShopList
    phoneLogin, // 手机号登录 /_hotel/phone-login
    smsLogin, // 验证码登录 /_hotel/sms-login
    sendVerifyCode, // 获取验证码 /_hotel/sms/send-verify-code
    getUserConfigApi
} from '@/requestPub/interface';
import { useUserStore } from '@/stores/user';
import { exquite_mqtt, subscribe_with_shop } from '../mqtt';
import { useDoorStore } from './door';
import { useConsumptionStore } from './consumption';
import { usePublicStore } from './public';
import { useLoading } from '@/hooks/useLoading';
const { showLoading, hideLoading } = useLoading();

export const useLoginStore = defineStore('login', () => {
    let login_success_data = null;
    // 状态：记住密码
    const isRememberPwd = ref(uni.getStorageSync('isRememberPwd') || false);
    // 登录token
    const loginToken = ref(uni.getStorageSync('loginToken') || '');
    // 当前门店
    const currentShopId = ref(uni.getStorageSync('shopId') || '');
    const currentShopInfo = ref(uni.getStorageSync('shopInfo') || {});
    const machineInfo = ref(uni.getStorageSync('machineInfo') || {});
    const setMachineInfo = (info) => {
        machineInfo.value = info || {};
        uni.setStorageSync('machineInfo', info || {});
    };
    // 保存token
    const setLoginToken = (token) => {
        loginToken.value = token || '';
        if (token) uni.setStorageSync('loginToken', token);
        else uni.removeStorageSync('loginToken');
    };

    // 设置记住密码状态
    const setIsRememberPwd = (status) => {
        isRememberPwd.value = !!status;
        uni.setStorageSync('isRememberPwd', !!status);
    };

    // 退出登录
    const exitLogin = async () => {
        try {
            exquite_mqtt();
            loginToken.value = '';
            uni.removeStorageSync('loginToken');

            // 清除用户信息
            await useUserStore().setUserInfo({});
            await useUserStore().setMqttInfo({});

            // 清除门店信息
            currentShopInfo.value = {};
            currentShopId.value = '';
            uni.removeStorageSync('shopInfo');
            uni.removeStorageSync('shopId');

            //清空桌台的页面类型 + 消费类型
            await useDoorStore().setTableType('index');
            await useDoorStore().setPayType('');
            await useConsumptionStore().setConsumptionType('index');
            await useConsumptionStore().setPayConsumptionType('');
            usePublicStore().tabsNav = 'table';
            return true;
        } catch (error) {
            console.log(error);
        }
    };
    // 设置门店ID
    const setShopId = (id) => {
        currentShopId.value = id || '';
        uni.setStorageSync('shopId', id || '');
    };
    // 设置当前门店
    const setCurrentShop = async (shopInfo, mqtt = {}) => {
        currentShopInfo.value = shopInfo || {};
        uni.setStorageSync('shopInfo', shopInfo || {});
        currentShopId.value = shopInfo?.id || '';
        uni.setStorageSync('shopId', shopInfo?.id || '');
        // 设置用户信息
        const userInfo = await getUserConfigApi();
        useUserStore().setUserInfo(userInfo.data);
        //设置mqtt信息
        useUserStore().setMqttInfo(login_success_data?.mqtt || mqtt);
        usePublicStore().getShopConfig();
        if (shopInfo?.id) {
            console.log('切换门店订阅消息');
            subscribe_with_shop();
        }
    };

    // 获取当前账号门店列表
    const getAcountsShopList = async (query) => {
        try {
            const res = await getBrandShopList(query, '登录中');
            if (res?.code === 200 && res?.data?.length) {
                return res?.data || [];
            }
            return [];
        } catch (e) {
            return [];
        }
    };
    // 公共：登录成功后的处理
    const handleLoginSuccess = async ({ data }) => {
        // 设置token
        setLoginToken(data?.authorization);
        login_success_data = data;
    };

    // 统一登录入口：三种登录
    const loginApiMap = {
        account: login, // 账号登录
        phone: phoneLogin, // 手机号登录
        sms: smsLogin // 验证码登录
    };

    const buildApiPayload = (type, payload) => {
        if (type === 'account') {
            return {
                hid: payload.hid,
                username: payload.username,
                password: payload.password
            };
        }
        if (type === 'phone') {
            return {
                phone: payload.phone,
                password: payload.password
            };
        }
        if (type === 'sms') {
            return {
                phone: payload.phone,
                code: payload.smsCode
            };
        }
        return payload;
    };

    const doLogin = async (type, payload) => {
        const api = loginApiMap[type];
        try {
            showLoading('登录中');
            const reqData = buildApiPayload(type, payload);
            const res = await api(reqData);
            if (res?.code !== 200) {
                return {
                    ok: false,
                    msg: res?.message || '登录失败'
                };
            }
            await handleLoginSuccess(res);
            // 根据用户选择是否记住密码设置状态
            setIsRememberPwd(payload.remember.length > 0);
            return {
                ok: true,
                msg: ''
            };
        } catch (e) {
            return {
                ok: false,
                msg: e
            };
        } finally {
            hideLoading();
        }
    };

    // 获取验证码接口
    const sendVerifyCodeFun = async (phone) => {
        try {
            const res = await sendVerifyCode({
                type: 2,
                phone
            });
            return res;
        } catch (e) {
            return {
                code: -1,
                msg: '获取验证码失败'
            };
        }
    };

    // 初始化是否记住密码的登录状态
    const initLoginFun = async () => {
        if (isRememberPwd.value && loginToken.value && currentShopId.value) {
            return true;
        }
        return false;
    };

    return {
        // 状态
        isRememberPwd,
        loginToken,
        currentShopId,
        currentShopInfo,

        // 基础方法
        setLoginToken,
        setIsRememberPwd,
        exitLogin,
        setCurrentShop,
        setShopId,
        // 门店
        getAcountsShopList,

        // 登录
        doLogin,

        // 验证码
        sendVerifyCodeFun,

        // 初始化
        initLoginFun,
        machineInfo,
        setMachineInfo
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useLoginStore, import.meta.hot));
}
